import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Search, AlertCircle, CheckCircle2, Loader2, UserMinus, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';

interface User {
  id: string;
  name: string;
  email: string;
  role: 'CEO' | 'GROUP_MANAGER' | 'MANAGER' | 'EXECUTIVE';
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  department?: string;
  position?: string;
}

interface ReassignAndDeactivateModalProps {
  open: boolean;
  onClose: () => void;
  user: User;
  onComplete: () => void;
}

export const ReassignAndDeactivateModal: React.FC<ReassignAndDeactivateModalProps> = ({
  open,
  onClose,
  user,
  onComplete
}) => {
  const [leadCount, setLeadCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [reassignmentResult, setReassignmentResult] = useState<{
    leadsReassigned: number;
    tasksReassigned: number;
  } | null>(null);

  // Fetch preview on open
  useEffect(() => {
    if (open) {
      fetchPreview();
      fetchUsers();
    } else {
      // Reset state when closed
      setLeadCount(0);
      setTaskCount(0);
      setSelectedUserId(null);
      setSearchQuery('');
      setError(null);
      setShowConfirmation(false);
      setShowSuccess(false);
      setReassignmentResult(null);
    }
  }, [open]);

  // Filter users based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(
      (u) =>
        u.name.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query) ||
        u.role.toLowerCase().includes(query) ||
        u.department?.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  const fetchPreview = async () => {
    try {
      setIsFetchingPreview(true);
      setError(null);

      const preview = await api.getOffboardPreview(user.id);
      setLeadCount(preview.leadCount);
      setTaskCount(preview.taskCount);
    } catch (err: any) {
      console.error('Error fetching offboard preview:', err);
      setError('Failed to load preview data. Please try again.');
    } finally {
      setIsFetchingPreview(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsFetchingUsers(true);
      setError(null);

      const data = await api.getUsersWithFilters({ status: 'ACTIVE', limit: 100 });

      // Handle response structure
      const usersArray = Array.isArray(data) ? data : data.users || [];

      // Filter out the source user
      const availableUsers = usersArray.filter((u: User) => u.id !== user.id);

      setUsers(availableUsers);
      setFilteredUsers(availableUsers);
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setIsFetchingUsers(false);
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
    setError(null);
  };

  const handleProceed = () => {
    // If no leads or tasks, skip to confirmation
    if (leadCount === 0 && taskCount === 0) {
      handleDirectDeactivate();
      return;
    }

    // If leads or tasks exist, require target user selection
    if (!selectedUserId) {
      setError('Please select a target user');
      return;
    }

    setShowConfirmation(true);
  };

  const handleDirectDeactivate = async () => {
    try {
      setIsLoading(true);
      setError(null);

      await api.updateUser(user.id, { status: 'INACTIVE' });

      // Show success
      setReassignmentResult({ leadsReassigned: 0, tasksReassigned: 0 });
      setShowSuccess(true);

      // Close after delay
      setTimeout(() => {
        handleClose();
        onComplete();
      }, 2000);
    } catch (err: any) {
      console.error('Error deactivating user:', err);
      setError(err.message || 'Failed to deactivate user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const confirmOffboarding = async () => {
    if (!selectedUserId) return;

    try {
      setIsLoading(true);
      setError(null);

      const result = await api.offboardUser(user.id, selectedUserId);

      // Show success
      setReassignmentResult({
        leadsReassigned: result.leadsReassigned || 0,
        tasksReassigned: result.tasksReassigned || 0
      });
      setShowSuccess(true);

      // Close after delay
      setTimeout(() => {
        handleClose();
        onComplete();
      }, 2000);
    } catch (err: any) {
      console.error('Error offboarding user:', err);
      setError(err.message || 'Failed to offboard user. Please try again.');
      setShowConfirmation(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return; // Prevent closing during operation
    onClose();
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'CEO':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'GROUP_MANAGER':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'MANAGER':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'EXECUTIVE':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getRoleLabel = (role: string) => {
    return role.replace('_', ' ');
  };

  const selectedUser = users.find((u) => u.id === selectedUserId);

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="bg-white shadow-2xl h-full flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center">
                    <UserMinus className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Deactivate User</h2>
                    <p className="text-sm text-gray-600">{user.name} - {user.email}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Warning Banner */}
              {isFetchingPreview ? (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg flex items-center space-x-2">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                  <p className="text-sm text-gray-600">Loading preview...</p>
                </div>
              ) : (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900">
                        Deactivating {user.name}
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        {leadCount > 0 || taskCount > 0 ? (
                          <>
                            <span className="font-semibold">{leadCount} leads</span> and{' '}
                            <span className="font-semibold">{taskCount} tasks</span> will be reassigned
                          </>
                        ) : (
                          'This user has no leads or tasks'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {showSuccess ? (
                /* Success Screen */
                <div className="py-8 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Offboarding Complete!
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    {reassignmentResult ? (
                      <>
                        {reassignmentResult.leadsReassigned} leads and {reassignmentResult.tasksReassigned} tasks
                        {selectedUser ? ` reassigned to ${selectedUser.name}` : ' processed'}.
                        <br />
                        {user.name} has been deactivated.
                      </>
                    ) : (
                      `${user.name} has been deactivated.`
                    )}
                  </p>
                </div>
              ) : showConfirmation ? (
                /* Confirmation Screen */
                <div className="py-8">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="h-8 w-8 text-yellow-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Confirm Offboarding
                    </h3>
                    <p className="text-sm text-gray-600">
                      Are you sure you want to proceed?
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-xs font-medium text-gray-500">User Being Deactivated</p>
                      <p className="text-sm font-medium text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>

                    {selectedUser && (
                      <div>
                        <p className="text-xs font-medium text-gray-500">Reassigning To</p>
                        <p className="text-sm font-medium text-gray-900">{selectedUser.name}</p>
                        <p className="text-xs text-gray-500">{selectedUser.email}</p>
                      </div>
                    )}

                    <div className="pt-3 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 mb-2">Summary</p>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• {leadCount} leads will be reassigned</li>
                        <li>• {taskCount} tasks will be reassigned</li>
                        <li>• {user.name} will be set to INACTIVE status</li>
                      </ul>
                    </div>
                  </div>
                </div>
              ) : (
                /* Selection Screen */
                <>
                  {leadCount === 0 && taskCount === 0 ? (
                    <div className="text-center py-8">
                      <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-sm text-gray-600 mb-4">
                        This user has no leads or tasks. Click "Deactivate" to proceed.
                      </p>
                    </div>
                  ) : (
                    <>
                      {/* Search */}
                      <div className="mb-4">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            type="text"
                            placeholder="Search by name, email, or role..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            disabled={isFetchingUsers || isLoading}
                          />
                        </div>
                      </div>

                      {/* User List */}
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700 mb-2">
                          Select Target User ({filteredUsers.length} available)
                        </p>

                        {isFetchingUsers ? (
                          <div className="text-center py-8">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                            <p className="text-sm text-gray-500 mt-2">Loading users...</p>
                          </div>
                        ) : filteredUsers.length === 0 ? (
                          <div className="text-center py-8">
                            <Users className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                            <p className="text-sm text-gray-500">
                              {searchQuery ? 'No users found matching your search' : 'No active users available'}
                            </p>
                          </div>
                        ) : (
                          <div className="max-h-[300px] overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
                            {filteredUsers.map((targetUser) => {
                              const isSelected = targetUser.id === selectedUserId;

                              return (
                                <button
                                  key={targetUser.id}
                                  onClick={() => handleSelectUser(targetUser.id)}
                                  disabled={isLoading}
                                  className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                                    isSelected
                                      ? 'border-red-500 bg-red-50'
                                      : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'
                                  }`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <div
                                      className={`w-10 h-10 ${
                                        isSelected ? 'bg-red-500' : 'bg-gray-400'
                                      } rounded-full flex items-center justify-center flex-shrink-0`}
                                    >
                                      <span className="text-white text-sm font-medium">
                                        {targetUser.name.charAt(0)}
                                      </span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-2">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {targetUser.name}
                                        </p>
                                        {isSelected && (
                                          <CheckCircle2 className="h-4 w-4 text-red-500 flex-shrink-0" />
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-500 truncate">{targetUser.email}</p>
                                      <div className="flex items-center space-x-2 mt-1">
                                        <Badge className={`text-xs ${getRoleBadgeColor(targetUser.role)}`}>
                                          {getRoleLabel(targetUser.role)}
                                        </Badge>
                                        {targetUser.department && (
                                          <span className="text-xs text-gray-400">{targetUser.department}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-2">
                      <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              {showSuccess ? (
                <Button onClick={handleClose} className="w-full">
                  Close
                </Button>
              ) : showConfirmation ? (
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirmation(false)}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={confirmOffboarding}
                    disabled={isLoading}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Confirm Offboarding
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleProceed}
                    disabled={isLoading || isFetchingPreview || (leadCount > 0 && !selectedUserId) || (taskCount > 0 && !selectedUserId)}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    <UserMinus className="h-4 w-4 mr-2" />
                    {leadCount === 0 && taskCount === 0 ? 'Deactivate' : 'Continue'}
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
